import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';

export interface FilterPanelOption {
  label: string;
  value: string;
  avatarUrl?: string;
}

export type TimeFrameType = 'Y' | 'M' | 'D' | 'All' | 'Custom';

export type FilterPanelField = 'project' | 'user' | 'timeFrame' | 'dateRange' | 'almProvider' | 'model';

export interface FilterPanelValue {
  project: string | null;
  user: string | null;
  timeFrame: TimeFrameType;
  dateRange: [Date, Date] | null;
  almProvider: string | null;
  model: string | null;
  changedField: FilterPanelField;
}

@Component({
  selector: 'app-filter-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, NzSelectModule, NzDatePickerModule, MatIconModule],
  templateUrl: './filter-panel.component.html',
  styleUrl: './filter-panel.component.scss',
})
export class FilterPanelComponent implements OnInit, OnChanges {
  @Input() projects: FilterPanelOption[] = [];
  @Input() users: FilterPanelOption[] = [];
  @Input() almProviders: FilterPanelOption[] = [];
  @Input() models: FilterPanelOption[] = [];

  @Input() loading: { projects?: boolean; users?: boolean; almProviders?: boolean; models?: boolean } = {};

  @Input() initialValues: Partial<FilterPanelValue> = {};

  @Output() filterChange = new EventEmitter<FilterPanelValue>();

  readonly timeFrameOptions: TimeFrameType[] = ['Y', 'M', 'D', 'All'];

  selectedProject: string | null = null;
  selectedUser: string | null = null;
  selectedTimeFrame: TimeFrameType = 'All';
  dateRange: [Date, Date] | null = null;
  selectedAlmProvider: string | null = null;
  selectedModel: string | null = null;

  showDatePicker = false;

  ngOnInit(): void {
    this.applyInitialValues();
    this.computeDateRangeFromTimeFrame(this.selectedTimeFrame);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValues']) {
      this.applyInitialValues();
    }
  }

  private applyInitialValues(): void {
    const v = this.initialValues;
    if (v.project !== undefined) this.selectedProject = v.project;
    if (v.user !== undefined) this.selectedUser = v.user;
    if (v.timeFrame !== undefined) this.selectedTimeFrame = v.timeFrame;
    if (v.dateRange !== undefined) this.dateRange = v.dateRange;
    if (v.almProvider !== undefined) this.selectedAlmProvider = v.almProvider;
    if (v.model !== undefined) this.selectedModel = v.model;
  }

  get pickerMode(): 'date' | 'month' | 'year' {
    if (this.selectedTimeFrame === 'M') return 'month';
    if (this.selectedTimeFrame === 'Y') return 'year';
    else return 'date';
  }

  selectTimeFrame(frame: TimeFrameType): void {
    if (this.selectedTimeFrame === frame) {
      return;
    }
    this.selectedTimeFrame = frame;
    this.dateRange = null;
    this.computeDateRangeFromTimeFrame(frame);
    this.emitChange('timeFrame');
  }

  private computeDateRangeFromTimeFrame(frame: TimeFrameType): void {
    const baseDate = this.dateRange?.[0] || new Date();
    const start = new Date(baseDate);
    const end = new Date(this.dateRange?.[1] || baseDate);

    switch (frame) {
      case 'D':
        // Only set default range if no existing dateRange
        if (!this.dateRange) {
          const today = new Date();
          start.setTime(today.getTime());
          start.setDate(today.getDate() - 6);
          end.setTime(today.getTime());
        }
        break;
      case 'M':
        start.setDate(1);
        // Last day of the selected month
        end.setMonth(end.getMonth() + 1, 0);
        break;
      case 'Y':
        start.setMonth(0);
        start.setDate(1);
        // Set to December 31
        end.setMonth(11, 31);
        break;
      case 'All':
        this.dateRange = null;
        return;
      default:
        return;
    }
    this.dateRange = [start, end];
  }

  onDateRangeChange(range: [Date, Date]): void {
    this.dateRange = range;
    this.showDatePicker = false;
    this.computeDateRangeFromTimeFrame(this.selectedTimeFrame);
    this.emitChange('dateRange');
  }

  onFieldChange(field: FilterPanelField): void {
    if (field === 'project') {
      this.selectedUser = null;
      this.selectedTimeFrame = 'All';
      this.dateRange = null;

      // Keep the LLM filters when "Select All" is chosen so they continue to apply globally.
      if (this.selectedProject) {
        this.selectedAlmProvider = null;
        this.selectedModel = null;
      }
    }
    if (field === 'almProvider') {
      this.selectedModel = null;
    }
    this.emitChange(field);
  }

  getFormattedDateRange(): string {
    if (!this.dateRange) return 'All time';
    const [start, end] = this.dateRange;
    return `${this.formatDate(start)} - ${this.formatDate(end)}`;
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  getSelectedUserAvatar(): string | undefined {
    return this.users.find((u) => u.value === this.selectedUser)?.avatarUrl;
  }

  private emitChange(changedField: FilterPanelField): void {
    this.filterChange.emit({
      project: this.selectedProject,
      user: this.selectedUser,
      timeFrame: this.selectedTimeFrame,
      dateRange: this.dateRange,
      almProvider: this.selectedAlmProvider,
      model: this.selectedModel,
      changedField,
    });
  }
}
